<template>
  <ApolloMutation
    :mutation="(gql) => createStatusEventMutation"
    :variables="{
      input: {
        orderId: orderId,
        status: selectedStatus,
      },
    }"
    @done="onDone"
  >
    <template #default="{ loading, mutate, error }">
      <div class="status-event-section">
        <div class="d-flex align-center mb-4">
          <h4>{{ $t('transactionReports.fulfillmentStatus') }}</h4>
          <v-chip
            v-if="currentStatus"
            small
            class="ml-3"
            :color="resolution === 'Open' ? 'warning' : 'success'"
            text-color="textOnPrimary"
          >
            {{ $t('transactionReports.resolution.' + resolution.toLowerCase()) }}
          </v-chip>
          <v-chip
            v-if="currentStatus"
            small
            class="ml-2"
            outlined
          >
            {{ $t('transactionReports.statuses.' + currentStatus) }}
          </v-chip>
        </div>

        <div
          v-if="statusEvents.length > 0"
          class="mb-4"
        >
          <h4 class="mb-2">
            {{ $t('transactionReports.statusTimeline') }}
          </h4>
          <v-timeline
            dense
            align-top
          >
            <v-timeline-item
              v-for="event in statusEvents"
              :key="event.id"
              small
              :color="event.status === currentStatus ? 'primary' : 'grey'"
            >
              <div class="d-flex align-center">
                <strong class="mr-2">{{ $t('transactionReports.statuses.' + event.status) }}</strong>
                <span class="text--secondary text-caption">{{ formatEventDate(event.createdAt) }}</span>
              </div>
            </v-timeline-item>
          </v-timeline>
        </div>

        <apollo-error-alert
          v-if="error"
          :error="error"
        />

        <div class="d-flex align-center">
          <v-select
            v-model="selectedStatus"
            :items="statusOptions"
            :label="$t('transactionReports.updateStatus')"
            outlined
            dense
            class="mr-3"
            style="max-width: 300px"
          />
          <v-btn
            tile
            color="primary"
            elevation="0"
            :disabled="!selectedStatus || loading"
            :loading="loading"
            @click="handleSubmit(mutate)"
          >
            {{ $t('transactionReports.updateStatus') }}
          </v-btn>
        </div>

        <v-dialog
          v-model="showSkipWarning"
          max-width="400"
        >
          <v-card>
            <v-card-text class="pt-4">
              {{ $t('transactionReports.skipStatusWarning', { skipped: skippedStatusNames }) }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                text
                @click="showSkipWarning = false"
              >
                {{ $t('common.cancel') }}
              </v-btn>
              <v-btn
                color="primary"
                text
                @click="confirmSubmit"
              >
                {{ $t('common.submit') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </template>
  </ApolloMutation>
</template>

<script>
import gql from 'graphql-tag';
import ApolloErrorAlert from '@/components/ApolloErrorAlert';
import { formatDate } from '@/helpers/generalFunctions';

export default {
  name: "AddOrderFulfillmentStatusEvent",
  components: {
    ApolloErrorAlert,
  },
  props: {
    orderId: {
      type: String,
      required: true,
    },
    currentStatus: {
      type: String,
      default: null,
    },
    resolution: {
      type: String,
      default: null,
    },
    validStatuses: {
      type: Array,
      default: () => [],
    },
    statusEvents: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedStatus: null,
      showSkipWarning: false,
      pendingMutate: null,
    };
  },
  computed: {
    createStatusEventMutation() {
      return gql`
        mutation createOrderFulfillmentStatusEvent($input: createOrderFulfillmentStatusEventInput!) {
          createOrderFulfillmentStatusEvent(input: $input) {
            orderFulfillmentStatusEvent {
              id
              status
              createdAt
            }
          }
        }
      `;
    },
    statusOptions() {
      return this.validStatuses.map(status => ({
        text: this.$t('transactionReports.statuses.' + status),
        value: status,
      }));
    },
    skippedStatuses() {
      if (!this.currentStatus || !this.selectedStatus) return [];
      const BRANCH_STATUSES = ['unable_to_deliver', 'waiting_for_stock'];
      const currentIndex = this.validStatuses.indexOf(this.currentStatus);
      const selectedIndex = this.validStatuses.indexOf(this.selectedStatus);
      if (selectedIndex <= currentIndex + 1 || currentIndex === -1) return [];
      return this.validStatuses
        .slice(currentIndex + 1, selectedIndex)
        .filter(s => !BRANCH_STATUSES.includes(s));
    },
    skippedStatusNames() {
      return this.skippedStatuses
        .map(s => this.$t('transactionReports.statuses.' + s))
        .join(', ');
    },
  },
  methods: {
    handleSubmit(mutate) {
      if (this.skippedStatuses.length > 0) {
        this.pendingMutate = mutate;
        this.showSkipWarning = true;
      } else {
        mutate();
      }
    },
    confirmSubmit() {
      this.showSkipWarning = false;
      if (this.pendingMutate) {
        this.pendingMutate();
        this.pendingMutate = null;
      }
    },
    onDone() {
      this.selectedStatus = null;
      this.$emit('status-updated');
    },
    formatEventDate: formatDate,
  },
}
</script>

<style scoped>
.status-event-section {
  border: 1px solid var(--v-lightAccent-base);
  padding: 16px;
  margin-bottom: 16px;
}
</style>
